import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, Loader2, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  emailConnectionApi,
  EmailConnection
} from '../api/emailApi';
import { YahooSMTPModal } from './YahooSMTPModal';

export const EmailIntegration: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [connection, setConnection] = useState<EmailConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [yahooModalOpen, setYahooModalOpen] = useState(false);
  const [microsoftConnecting, setMicrosoftConnecting] = useState(false);
  const [googleConnecting, setGoogleConnecting] = useState(false);

  // Fetch email connection status
  useEffect(() => {
    const fetchConnection = async () => {
      try {
        const data = await emailConnectionApi.getActive();
        setConnection(data as EmailConnection | null);
      } catch (err) {
        console.error('Failed to fetch email connection:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConnection();
  }, []);

  // Handle OAuth success message
  useEffect(() => {
    const provider = searchParams.get('provider');
    const success = searchParams.get('success');

    if (provider && success === 'true') {
      toast.success(`${provider} account connected successfully!`);
      
      // Refresh connection data
      const refetchConnection = async () => {
        const data = await emailConnectionApi.getActive();
        setConnection(data as EmailConnection | null);
      };
      refetchConnection();

      // Clear search params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect this email provider?')) {
      return;
    }

    setDisconnecting(true);
    try {
      await emailConnectionApi.disconnect();
      setConnection(null);
      toast.success('Email provider disconnected successfully');
    } catch (err) {
      toast.error('Failed to disconnect email provider');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleMicrosoftConnect = async () => {
    setMicrosoftConnecting(true);
    try {
      const response = await emailConnectionApi.connectMicrosoft();

      if (response.authUrl) {
        window.location.href = response.authUrl;
      }
    } catch (err) {
      setMicrosoftConnecting(false);
      toast.error('Failed to connect to Microsoft. Please try again.');
    }
  };

  const handleGoogleConnect = async () => {
    setGoogleConnecting(true);
    try {
      const response = await emailConnectionApi.connectGoogle();
      if (response.authUrl) {
        window.location.href = response.authUrl;
      }
      
    } catch (err) {
      setGoogleConnecting(false);
      toast.error('Failed to connect to Google. Please try again.');
    }
  };

  const handleYahooSuccess = async () => {
    const data = await emailConnectionApi.getActive();
    setConnection(data as EmailConnection | null);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
        <Loader2 className="h-8 w-8 animate-spin mb-2 text-strivre-burgundy" />
        <p>Loading email settings...</p>
      </div>
    );
  }

  return (
    <div className="w-full animate-fade-in space-y-6">
      {/* Status Card */}
      <div className="bg-white border border-strivre-lightnavy rounded-2xl overflow-hidden shadow-sm">
        <div className="p-6 border-b border-slate-100 bg-slate-50/30">
          <div className="flex items-center justify-between">
            <div className="text-left">
              <h3 className="text-lg font-bold text-slate-900">Email Integration</h3>
              <p className="text-sm text-slate-500">Connect email providers to send and receive messages.</p>
            </div>
            <Mail className="h-8 w-8 text-strivre-burgundy" />
          </div>
        </div>

        <div className="p-6">
          {/* Current Status */}
          <div className={`mb-6 p-4 rounded-xl border-2 transition-all ${
              connection
                ? 'border-green-200 bg-green-50'
                : 'border-red-200 bg-red-50'
            }`}
          >
            <div className="flex items-center gap-3 text-left">
              {connection ? (
                <>
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-green-900">Connected</p>
                    <p className="text-sm text-green-700">
                      {connection.provider === 1
                        ? 'Microsoft' 
                        : connection.provider === 2
                        ? 'Google' 
                        : 'Yahoo'} • {connection.email}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <XCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-red-900">Not Connected</p>
                    <p className="text-sm text-red-700">Connect an email provider to get started</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {connection && (
            <div className="mb-6 flex gap-3">
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="flex items-center gap-2 px-6 py-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl font-semibold transition-all disabled:opacity-50 border border-red-200"
              >
                {disconnecting ? (
                  <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                ) : (
                  <Trash2 size={18} />
                )}
                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Provider Options - Only show if not connected */}
      {!connection && (
        <div className="bg-white border border-strivre-lightnavy rounded-2xl overflow-hidden shadow-sm">
          <div className="p-6 border-b border-slate-100 bg-slate-50/30">
            <h3 className="text-lg font-bold text-slate-900">Available Providers</h3>
            <p className="text-sm text-slate-500">Choose an email provider to connect</p>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Microsoft */}
            <button
              onClick={handleMicrosoftConnect}
              disabled={microsoftConnecting}
              className="
                p-6
                border-2
                border-strivre-lightnavy
                hover:border-strivre-burgundy
                bg-white
                hover:bg-strivre-lightburgundy
                rounded-xl
                transition-all
                text-center
                group
                hover:shadow-md
                hover:-translate-y-0.5
                disabled:opacity-50
                disabled:cursor-not-allowed
              "
            >
              <div className="flex items-center justify-center h-16 mb-4 bg-strivre-lightburgundy group-hover:bg-strivre-lightburgundy rounded-xl transition-all">
                {microsoftConnecting ? (
                  <Loader2 className="w-8 h-8 text-strivre-burgundy animate-spin" />
                ) : (
                  <svg className="w-8 h-8 text-strivre-burgundy" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z" />
                  </svg>
                )}
              </div>
              <p className="font-bold text-slate-900 mb-1">{microsoftConnecting ? 'Connecting...' : 'Microsoft'}</p>
              <p className="text-xs text-slate-500">Outlook & Microsoft 365</p>
            </button>

            {/* Google */}
            <button
              onClick={handleGoogleConnect}
              disabled={googleConnecting}
              className="
                p-6
                border-2
                border-strivre-lightnavy
                hover:border-strivre-burgundy
                bg-white
                hover:bg-strivre-lightburgundy
                rounded-xl
                transition-all
                text-center
                group
                hover:shadow-md
                hover:-translate-y-0.5
                disabled:opacity-50
                disabled:cursor-not-allowed
              "
            >
              <div className="flex items-center justify-center h-16 mb-4 bg-strivre-lightburgundy group-hover:bg-strivre-lightburgundy rounded-xl transition-all">
                {googleConnecting ? (
                  <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
                ) : (
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path fill="#EA4335" d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0z" />
                    <path fill="#FBBC05" d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0z" />
                    <path fill="#34A853" d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0z" />
                    <path fill="#4285F4" d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0z" />
                    <text x="12" y="17" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">G</text>
                  </svg>
                )}
              </div>
              <p className="font-bold text-slate-900 mb-1">{googleConnecting ? 'Connecting...' : 'Google'}</p>
              <p className="text-xs text-slate-500">Gmail & Google Workspace</p>
            </button>

            {/* Yahoo */}
            <button
              onClick={() => setYahooModalOpen(true)}
              className="
                p-6
                border-2
                border-strivre-lightnavy
                hover:border-purple-400
                bg-white
                hover:bg-purple-50
                rounded-xl
                transition-all
                text-center
                group
                hover:shadow-md
                hover:-translate-y-0.5
              "
            >
              <div className="flex items-center justify-center h-16 mb-4 bg-purple-50 group-hover:bg-purple-100 rounded-xl transition-all">
                <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm-2 18h-2V8h2v10zm0-12h-2V6h2v2zm4 12h-2V8h2v10zm0-12h-2V6h2v2z" />
                </svg>
              </div>
              <p className="font-bold text-slate-900 mb-1">Yahoo</p>
              <p className="text-xs text-slate-500">Yahoo Mail</p>
            </button>
          </div>

          <div className="px-6 pb-6 text-xs text-slate-500">
            <p>💡 <strong>Tip:</strong> OAuth providers (Microsoft & Google) redirect you to their login page. Yahoo requires manual SMTP configuration.</p>
          </div>
        </div>
      )}

      {/* Yahoo Modal */}
      <YahooSMTPModal
        isOpen={yahooModalOpen}
        onClose={() => setYahooModalOpen(false)}
        onSuccess={handleYahooSuccess}
      />

      {/* Info Section */}
      <div className="bg-strivre-lightburgundy border border-strivre-lightnavy rounded-2xl p-6">
        <h4 className="font-bold text-strivre-burgundy mb-3 text-left">ℹ️ Security & Privacy</h4>
        <ul className="space-y-2 text-sm text-slate-700">
          <li className="flex gap-2">
            <span className="flex-shrink-0">✓</span>
            <span>Passwords are encrypted and stored securely</span>
          </li>
          <li className="flex gap-2">
            <span className="flex-shrink-0">✓</span>
            <span>We never store your OAuth tokens in plain text</span>
          </li>
          <li className="flex gap-2">
            <span className="flex-shrink-0">✓</span>
            <span>You can disconnect anytime without losing your data</span>
          </li>
        </ul>
      </div>
    </div>
  );
};
